import React, { useEffect, useState, useMemo, useContext } from "react"
import DragUploadInput from '../drag-upload-input/drag-upload-input'
import { uuidv4 } from '../../util/hash'
import styles from './styles.module.css'
import { useRunBlueprint } from "../../hooks/useRunBlueprint"
import { useDispatch, useSelector } from "../../stores/dashboard.store"
import Card from '../nura/card/card'
import Modal from '../modal/modal'
import useFlow from "../../hooks/useFlow"
import BaseForm from "../base-form/base-form"
import Button from '../nura/button/button'
import useFetch from "../../hooks/useFetch"
import { configFonts, fetchAllFromGallery, proxyAzureAiFoundryModels, proxyGetElevenVoices, proxyGetOpenAIModels } from "../../api-client/data"
import GallerySlider from "../slider/galleryslider"
import AddIcon from '@mui/icons-material/Add'
import CropperVideoPlayer from "../cropper-video-player/cropper-video-player"
import { CircularProgress, IconButton } from "@mui/material"
import { Alert, AlertTitle } from "@mui/material"
import { Actions } from "../../stores/dashboard.reducer"
import DeleteIcon from '@mui/icons-material/Delete'
import DownloadIcon from '@mui/icons-material/Download'
import { isValueSet, flatten } from '../../util/util'
import { formatEnvURL } from '../../util/env'
import RunBlueprintContext, { useInitRunBlueprint } from '../../context/run-blueprint.context'

const ListEntry = ({ onChange, children, name, value }) => {
    const [listValues, setListValues] = useState(!Array.isArray(value) ? { [uuidv4()]: "" } : value?.reduce((a, c) => {
        a[uuidv4()] = c
        return a
    }, {}))

    const listOnChange = (id, value) => {
        setListValues((prev) => ({
            ...prev,
            [id]: value,
        }))
    }

    const addListEntry = () => {
        setListValues((prev) => ({
            ...prev,
            [uuidv4()]: "",
        }))
    }

    useEffect(() => {
        if (onChange === undefined) {
            return
        }
        onChange(Object.values(listValues))
    }, [listValues])

    const deleteListEntry = (id) => {
        setListValues((prev) => Object.keys(prev).reduce((a, c) => {
            if (c === id) return a

            a[c] = prev[c]
            return a
        }, {}))
    }

    const listEntryCount = useMemo(() => Object.keys(listValues).length, [listValues])

    return (
        <div>
            <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', marginBottom: 8, marginTop: 15 }}>
                <div style={{ color: 'var(--text-primary)' ,flex: 1, textTransform: 'capitalize' }}>
                    {name}
                    {listEntryCount > 1 && (
                        <span style={{ paddingLeft: 5 }}>
                            ({listEntryCount})
                        </span>
                    )}
                </div>
                <Button onClick={addListEntry} style={{ borderRadius: 10, paddingTop: 5 }} variant="outline" size="small">
                    <AddIcon style={{ color: 'var(--text-secondary)' }} />
                </Button>
            </div>
            <div style={{ display: 'flex', flexDirection: 'column', gap: 10, maxHeight: 200, overflowY: 'auto', marginTop: 10 }}>
                {Object.keys(listValues).map((x, idx) => (
                    <div style={{ display: 'flex' }} key={x}>
                        <div style={{ width: '100%' }}>
                            {children((value) => listOnChange(x, value), listValues[x])}
                        </div>
                        {
                            idx !== 0 && (
                                <div>
                                    <IconButton onClick={() => deleteListEntry(x)}>
                                        <div style={{ color: `rgb(207, 22, 22)` }}>
                                            <DeleteIcon />
                                        </div>
                                    </IconButton>
                                </div>
                            )
                        }
                    </div>
                ))}
            </div>
        </div>
    )
}

export const StandardInput = ({ children, name }) => {
    return (
        <>
            <div className={styles.menuDescription}>
                <div style={{ color: 'var(--text-primary)',textTransform: 'capitalize', flex: 1 }}>{name}</div>
            </div>
            {children}
        </>
    )
}

const hexToRgb = (hex) => {
    const bigint = parseInt(hex.slice(1), 16)
    const r = (bigint >> 16) & 255
    const g = (bigint >> 8) & 255
    const b = bigint & 255
    return [r, g, b]
}

const rgbToHex = (rgb) => {
    return "#" + ((1 << 24) + (rgb[0] << 16) + (rgb[1] << 8) + rgb[2]).toString(16).slice(1)
}

const Positions = ({ onChange }) => {
    return (
        <select style={{ boxSizing: 'border-box', width: '100%' }} onChange={({ target: { value } }) => onChange(value)}>
            <option value="left-top">left top</option>
            <option value="center-top">center top</option>
            <option value="right-top">right top</option>
            <option value="left-center">left center</option>
            <option value="center-center">center center</option>
            <option value="right-center">right center</option>
            <option value="left-bottom">left bottom</option>
            <option value="center-bottom">center bottom</option>
            <option value="right-bottom">right bottom</option>
            <option value="mobile-center">mobile center</option>
        </select>
    )
}

const FontFamily = ({ onChange }) => {
    const fonts = useFetch(configFonts)

    return fonts.loading ? <CircularProgress /> : (
        <select style={{ boxSizing: 'border-box', width: '100%' }} onChange={({ target: { value } }) => onChange(value)}>
            {
                fonts?.data?.map(x => (
                    <option value={x}>{x}</option>
                ))
            }
        </select>
    )
}

const ElevenLabsVoices = ({ onChange, value }) => {
    const voices = useFetch(proxyGetElevenVoices)

    return voices.loading ? <CircularProgress /> : (
        <select style={{ boxSizing: 'border-box', width: '100%' }} value={value} onChange={({ target: { value } }) => onChange(value)}>
            {
                voices?.data?.map(x => (
                    <option value={x?.voice_id}>{x?.name}</option>
                ))
            }
        </select>
    )
}

const OpenAIModels = ({ onChange, value }) => {
    const voices = useFetch(proxyGetOpenAIModels)

    return voices.loading ? <CircularProgress /> : (
        <select style={{ boxSizing: 'border-box', width: '100%' }} value={value} onChange={({ target: { value } }) => onChange(value)}>
            {
                voices?.data?.map(x => (
                    <option value={x?.id}>{x?.id}</option>
                ))
            }
        </select>
    )
}


const AzureAIFoundryModels =({ onChange, value }) => {
    const voices = useFetch(proxyAzureAiFoundryModels)

    return voices.loading ? <CircularProgress /> : (
        <select style={{ boxSizing: 'border-box', width: '100%' }} value={value} onChange={({ target: { value } }) => onChange(value)}>
            {
                voices?.data?.map(x => (
                    <option value={x?.id}>{x?.id}</option>
                ))
            }
        </select>
    )
}

export function BlueprintInput({ formValues, type, value, onChange, name, setValidationError }) {
    const blueprintId = useSelector(x => x?.activeProject?.id)
    const galleryData = useFetch(() => fetchAllFromGallery(blueprintId))
    const defaultColor = '#000000'
    const [inputValue, setInputValue] = useState(type === "color" ? hexToRgb(value || defaultColor) : (value || ""))

    const handleChange = (newValue) => {
        setInputValue(newValue)
        onChange(newValue)
    }

    const handleScriptDownload = (e) => {
        e.preventDefault()
        const link = document.createElement("a")
        link.href = formatEnvURL("/config/content/script_example")
        link.download = "script_example.json"
        document.body.appendChild(link)

        link.click()

        document.body.removeChild(link)
    }

    switch (type) {
        case "openaimodel": {
            return (
                <StandardInput name={name}>
                    <OpenAIModels onChange={handleChange} />
                </StandardInput>
            )
        }        
        case "11labsvoice": {
            return (
                <StandardInput name={name}>
                    <ElevenLabsVoices value={value} onChange={handleChange} />
                </StandardInput>
            )
        }
        case "position":
            return (
                <StandardInput name={name}>
                    <Positions onChange={handleChange} />
                </StandardInput>
            )
        case "graphic":
            return (
                <StandardInput name={name}>
                    <DragUploadInput
                        value={value}
                        onChange={handleChange}
                        accept="image/*"
                        setValidationError={setValidationError}
                    />
                </StandardInput>
            )
        case "audio":
            return (
                <StandardInput name={name}>
                    <DragUploadInput
                        value={value}
                        onChange={handleChange}
                        accept="audio/*"
                        setValidationError={setValidationError}                        
                    />
                </StandardInput>
            )
        case "video_path":
            return (
                <StandardInput name={name} style={{ position: 'relative' }}>
                    <DragUploadInput
                        value={inputValue}
                        onChange={handleChange}
                        accept="video/mp4"
                        setValidationError={setValidationError}
                    />
                </StandardInput>
            )
        case "script":
            return (
                <>
                    <div onClick={handleScriptDownload} style={{ marginTop: 30, display: 'flex', justifyContent: 'start', alignItems: 'center', gap: 5, color: 'var(--text-primary)', cursor: 'pointer' }}>
                        <DownloadIcon />
                        Download Template
                    </div>
                    <StandardInput name={name}>
                        <DragUploadInput value={value} onChange={handleChange} accept="json/*" />
                    </StandardInput>

                </>
            )
        case "number":
            return <StandardInput name={name}><input
                className={styles.textArea}
                rows={5}
                type="number"
                value={inputValue}
                onChange={({ target: { value } }) => handleChange(value)}
            /></StandardInput>
        case "gallery_video_path":
            return <StandardInput name={name}><GallerySlider galleryData={galleryData} onChange={handleChange} /></StandardInput>
        case "csv":
            return <StandardInput name={name}><DragUploadInput onFileUpload={handleChange} accept="csv/*" /></StandardInput>
        case "color":
            return <StandardInput name={name}><input
                type="color"
                value={rgbToHex(value || defaultColor)}
                onChange={({ target: { value } }) => {
                    handleChange(hexToRgb(value))
                }}
                style={{ cursor: "pointer", border: "none", outline: "none" }}
            /></StandardInput>
        case "screen_region":
            return (
                <StandardInput name={name}>
                    <CropperVideoPlayer
                        onCropAreaSelected={handleChange}
                        flatValues={flatten(Object.values(formValues))}
                    />
                </StandardInput>
            )
        case "font_family":
            return (
                <StandardInput name={name}>
                    <FontFamily onChange={handleChange} />
                </StandardInput>
            )
        default:
            return <ListEntry value={value} onChange={handleChange} name={name}>
                {(func, value) => <input
                    defaultValue={value}
                    className={styles.textArea}
                    rows={5}
                    onChange={({ target: { value } }) => func(value)}
                />}
            </ListEntry>
    }
}

export const RunBlueprintFlow = ({ inputs, flow }) => {
    const [validationError, setValidationError] = useState()
    const { isLoading } = useContext(RunBlueprintContext)

    const sortedInputs = useMemo(() => inputs.sort((a) => a.name === "region" ? 1 : -1), [inputs])

    const currentStepInput = useMemo(() => inputs[flow.step], [flow.step, sortedInputs])
    const initialFormValues = useMemo(() => flow.formValues, [sortedInputs])

    const [formValues, setFormValues] = useState(initialFormValues)

    const submitValues = () => {
        const stepValue = formValues[currentStepInput?.id]

        if (isValueSet(stepValue)) {
            flow.onClickChange(currentStepInput?.id)(formValues[currentStepInput?.id])
        }
    }

    function handleOnChange(id, newValue) {
        setFormValues((prev) => ({
            ...prev,
            [id]: newValue,
        }))
    }

    return currentStepInput ? (
        <BaseForm onBack={flow.handleBack} title={currentStepInput?.parent?.name}>
            <div className={styles.textAreaContainer}>
                <BlueprintInput
                    key={currentStepInput?.id + currentStepInput?.type}
                    type={currentStepInput?.type}
                    value={formValues[currentStepInput?.id]}
                    onChange={(value) => handleOnChange(currentStepInput?.id, value)}
                    name={currentStepInput?.name}
                    setValidationError={setValidationError}
                    formValues={formValues}
                />
                {validationError?.message !== undefined && (
                    <div style={{ color: 'red', marginTop: 7, fontSize: 14, width: 345 }}>
                        {validationError?.message}
                    </div>
                )}
            </div>
            <div className={styles.actionGroup}>
                <Button
                    onClick={submitValues}
                    style={{ width: '100%' }}
                    variant="outline"                    
                    disabled={isLoading}
                >
                    {
                        isLoading ? (
                            <CircularProgress size={20}/>
                        ) :
                        (
                            <>Next</>
                        )
                    }
                </Button>
            </div>
        </BaseForm>
    ) : (
        <BaseForm onBack={flow.handleBack} title="Run">
            <div className={styles.menuDescription}>
                <div>Are you sure you want to run?</div>
            </div>
            <div className={styles.actionGroup}>
                <Button
                    style={{ width: '100%' }}
                    variant="primary"
                    onClick={() => {
                        const vals = formValues
                        flow.finalStageSubmit()(vals)
                        setFormValues({})
                    }}
                >
                    Run
                </Button>
            </div>
        </BaseForm>
    )
}

const PendingRequestAlert = () => {
    return (
        <Alert icon={<CircularProgress style={{ padding: 10 }} size={40} />} severity="info">
            <AlertTitle>Processing Your Request</AlertTitle>
            Do not refresh this page.
        </Alert>
    )
}

const RunBlueprintDialog = ({ activeProject }) => {
    const { executeBlueprint } = useRunBlueprint(activeProject?.specificVersionId)
    const { showRunDialog } = useSelector()
    const dispatch = useDispatch()
    const [isPending, setIsPending] = useState(false)
    const runBlueprint = useInitRunBlueprint()

    useEffect(() => {
        flow.setStep(0)
        setIsPending(false)
    }, [showRunDialog])

    const inputs = useMemo(() => activeProject?.io?.inputs.map(x => {
        const parent = activeProject?.nodes?.find(k => k?.id === x?.nodeId)
        return ({
            ...x,
            parent: parent?.data
        })
    }), [activeProject])

    const handleRunBlueprint = (values) => {
        setIsPending(true)
        const childToParent = activeProject?.nodes.reduce((a, c) => {
            for (const p in c?.data?.inputs) {
                const input = c?.data?.inputs[p]
                a[input?.id] = c?.id
            }
            return a
        }, {})

        const nodeValues = Object.keys(values).reduce((a, c) => {
            const parentId = childToParent[c]

            if (a[parentId] === undefined) {
                a[parentId] = {}
            }

            a[parentId] = {
                ...a[parentId],
                [c]: values[c],
            }

            return a
        }, {})

        executeBlueprint(nodeValues, () => {
            dispatch(Actions.SetShowRunDialog, false)
            setIsPending(false)
        })
    }

    const onBack = () => dispatch(Actions.SetShowRunDialog, false)
    const onSubmit = handleRunBlueprint
    const flow = useFlow({ onBack, onSubmit, initialValues: activeProject?.defaultValues })

    return (
        <Modal show={showRunDialog} style={{ minWidth: 358, height: 'auto', maxWidth: 800 }} onClose={() => dispatch(Actions.SetShowRunDialog, false)}>
            <RunBlueprintContext.Provider value={runBlueprint}>
                <Card variant="modal" style={{ minWidth: 358, height: 'auto', maxWidth: 800 }}>
                    {
                        isPending ? <PendingRequestAlert /> : (
                            <RunBlueprintFlow
                                inputs={inputs}
                                flow={flow}
                            />
                        )
                    }

                </Card>
            </RunBlueprintContext.Provider>
        </Modal>
    )
}

export default () => {
    const { showRunDialog, runProject, activeProject } = useSelector()
    if (!showRunDialog) return null

    return <RunBlueprintDialog
        activeProject={runProject || activeProject}        
    />
}
